import { formatClock, formatDate, formatMoney, t } from "@hakili/i18n";
import {
  Button,
  HakiliMascot,
  MascotBadge,
  type MascotState,
  Skeleton,
  SkeletonRegion,
  StateBadge,
} from "@hakili/ui";
import { type ReactElement, type ReactNode } from "react";

import { type AnalysisView, GradeCard, OpinionPanel, SecondLook, type SecondLookBody } from "./analyse.js";
import { incompleteCauseOf } from "./incomplete-cause.js";
import { asSentence } from "../sentence.js";
import { LabelledSentence } from "../labelled-sentence.js";
import { fill } from "./dossiers.js";

/**
 * `F-2` (BANK-V2, D2 §5 and the bay frames): **Hakili's bay, the one persistent column** of every dossier screen.
 *
 * The underwriter is shown doing only what the record says (D0 §1.1, D-1020-7): reading the pièces, proposing the points, the
 * grade with every operand, the avis, answering a question with its sources named. It moves no stage and decides nothing;
 * nothing that is not built is drawn (no precedents, no win probability, no « simuler » it cannot do).
 *
 * **It binds ONLY to the bank's projection** (`lending/domain/bank-views.ts`, BANK-IDS): `AnalysisView` is parsed from that
 * answer field by field, so a run id, a prompt or model identifier, a rule name or a digest has no field to land in and
 * no place to be printed. The five states of the face are driven by the run's real state, never by a timer.
 */
export type BayContext = "nouveau" | "revue" | "composer" | "decision" | "conditions" | "quiet";

/** The context a stage of the file puts the bay in. The composer is the review's last step, so it is the review with `composer`. */
export function bayContextFor(stage: string, composer: boolean): BayContext {
  if (stage === "nouveau") return "nouveau";
  if (stage === "revue") return composer ? "composer" : "revue";
  if (stage === "conditions") return "conditions";
  return "quiet";
}

export type RunStanding = "reading" | "failed" | "read" | "none" | "unavailable";

/** Where the run stands: from the status and outcome of the answer, `none` when the bank has no analysis for the file. */
export function runStanding(
  analysis: AnalysisView | null,
  loaded: boolean,
  unavailable = false,
): RunStanding {
  if (!loaded) return "reading";
  if (unavailable) return "unavailable";
  if (analysis === null) return "none";
  if (analysis.run.outcome === "en_echec") return "failed";
  if (analysis.run.status === "en_cours" || analysis.run.status === "en_attente") return "reading";
  return "read";
}

/** What the screen knows that the analysis does not: the points she flagged in the review, a derogation on the chef's file. */
export interface BayFlags {
  readonly flaggedPoints?: number;
  readonly derogation?: boolean;
  /**
   * `#778` (D2 `bay.js:20-21`): in the composer the face reads the LIVE composition, `compose(...).gaps.length || .blocks.length`. `true` when it holds a block or a gap D2's
   * `compose` lists (the missing-row case is D2's separate box, `analyst.js:160`, not a gap of its own: the screen leaves it out); `false` when it holds none; `null` while no
   * rate is typed, so there is no composition and the withheld proposal still flags.
   */
  readonly composerFlagging?: boolean | null;
}

/**
 * The face's state, by the context and the run , **D2's own rule** (`bay.js` `mascotState`, frames 13, 15, 34, 35): reading while it
 * runs, failed when it could not finish; **done on the nouveau file** whatever the note says; **in the review at rest until she
 * flags a point**; **in the composer flagging when the proposal is withheld** (a note incomplète or provisional, no offer); on the
 * chef's decision flagging when the file carries a derogation. A quiet stage is at rest.
 */
export function mascotStateFor(
  context: BayContext,
  standing: RunStanding,
  analysis: AnalysisView | null,
  flags: BayFlags = {},
): MascotState {
  if (standing === "reading") return "reading";
  if (standing === "failed") return "failed";
  if (standing === "none" || standing === "unavailable" || context === "conditions" || context === "quiet")
    return "idle";
  if (context === "nouveau") return "done";
  if (context === "revue") return (flags.flaggedPoints ?? 0) > 0 ? "flagging" : "idle";
  if (context === "decision") return flags.derogation === true ? "flagging" : "idle";
  if (typeof flags.composerFlagging === "boolean") return flags.composerFlagging ? "flagging" : "idle";
  const grade = analysis?.grade ?? null;
  const withheld =
    analysis?.offer === null || (grade?.incomplete ?? null) !== null || grade?.provisional === true;
  return withheld ? "flagging" : "idle";
}

const STATE_WORDS = new Map<MascotState, Parameters<typeof t>[0]>([
  ["idle", "bank.bay.etatRepos"],
  ["reading", "bank.bay.etatLit"],
  ["done", "bank.bay.etatTermine"],
  ["flagging", "bank.bay.etatSignale"],
  ["failed", "bank.bay.etatEchec"],
]);

export interface UnderwriterBayProps {
  readonly context: BayContext;
  /** The answer of the analysis door, parsed; `null` while none exists. */
  readonly analysis: AnalysisView | null;
  /** The door has answered at least once for this file (before that, the bay shows its skeleton). */
  readonly loaded: boolean;
  /** `F-2`: the door itself did not answer (the service, not the run): said as such, with a way to ask again. */
  readonly unavailable?: boolean;
  readonly onRetry?: () => void;
  /** R81's sentence when the bank has no analysis for the file. */
  readonly sentence: string | null;
  readonly busy: boolean;
  /** Her second look by her own door. Absent on the chef's read. */
  readonly onSecondLook?: (body: SecondLookBody) => Promise<void>;
  /** Goes to « Politique »: given to a reader who has that screen. */
  readonly onPolitique?: () => void;
  /** `ARC2-BAY` (D2 `quietBay`): on the conditions stage the bay lists the dates to watch; the report's validity, from the file's own read. */
  readonly watch?: { readonly reportValidUntil: string | null };
  /** `ARC2-BAY` (D2 `usepropo`): « Reprendre la proposition », in the composer, puts Hakili's term and rate back in the composition. */
  readonly onUseProposal?: () => void;
  /** `ARC2-BAY`: the points she flagged in the review (the face flags while there is one). */
  readonly flaggedPoints?: number;
  /** `ARC2-BAY`: the chef's file carries a derogation (the face flags). */
  readonly derogation?: boolean;
  /** `#778`: what the composer's live composition says (see `BayFlags.composerFlagging`). */
  readonly composerFlagging?: boolean | null;
  /** The docked question box (already built by the page: it holds the thread and the door). Absent: no box is drawn. */
  readonly question?: ReactNode;
}

function Scene(props: { readonly state: MascotState }): ReactElement {
  return (
    <div data-bay="scene">
      <div data-bay="mascot">
        <HakiliMascot state={props.state} label={t("bank.bay.mascotte")} />
        {props.state === "idle" ? null : (
          <span data-bay="mascot-badge">
            <MascotBadge state={props.state} />
          </span>
        )}
      </div>
    </div>
  );
}

function Register(props: { readonly analysis: AnalysisView }): ReactElement | null {
  const rows = props.analysis.registre;
  if (rows === null || rows.length === 0) return null;
  const blocking = rows.filter((one) => one.severity === "bloquant").length;
  // D2 `bay.js` registerBlock: « 2 constats signalés, aucun bloquant », then the findings as plain sentences. A blocking one is counted.
  const summary =
    rows.length === 1
      ? t(blocking === 0 ? "bank.bay.registreUn" : "bank.bay.registreUnBloquantSeul")
      : blocking === 0
        ? fill("bank.bay.registreAucunBloquant", { n: String(rows.length) })
        : fill(blocking === 1 ? "bank.bay.registreUnBloquant" : "bank.bay.registreDesBloquants", {
            n: String(rows.length),
            k: String(blocking),
          });
  return (
    <details data-bay="registre" data-testid="bank-hakili-registre">
      <summary>{summary}</summary>
      <ul>
        {rows.map((one, index) => (
          // A finding has no identity the bank may hold; its place in the register is its key.
          <li key={index} data-severity={one.severity}>
            {asSentence(one.reason)}
          </li>
        ))}
      </ul>
    </details>
  );
}

/**
 * `ARC2-BAY` (D2 `bay.js` traceList): the four lines of the reading, always drawn, each with the clock time its step ended: the
 * pièces read, the points proposed (their count), the note (« incomplète » when the note is), the avis. The run's `offer` step is
 * not a line (D2 draws four). **« Lecture des pièces » has no step of its own, so no time** (a data gap, listed to the AA-AD lane).
 * A line is `on` once its step has ended; while the run reads, the first line is the one under way.
 */
function Trace(props: { readonly analysis: AnalysisView; readonly reading: boolean }): ReactElement {
  const { analysis } = props;
  const endOf = (kind: string): string | null =>
    analysis.steps.find((one) => one.kind === kind)?.endedAt ?? null;
  const clock = (iso: string | null): ReactNode => {
    if (iso === null || Number.isNaN(Date.parse(iso))) return null;
    return <time dateTime={iso}>{formatClock(new Date(iso))}</time>;
  };
  const count = analysis.points.length;
  const incomplete = (analysis.grade?.incomplete ?? null) !== null;
  const lines: readonly {
    readonly key: string;
    readonly on: boolean;
    readonly words: string;
    readonly at: string | null;
  }[] = [
    {
      key: "lecture",
      on: props.reading || analysis.steps.length > 0,
      words: t("bank.bay.traceLecture"),
      // `ARC2-DATA` (item 3): the instant the pièces were really read, as the door gives it; never a step's time. None when it does not know.
      at: analysis.run.documentsReadAt,
    },
    {
      key: "points",
      on: endOf("points") !== null,
      words:
        endOf("points") === null
          ? t("bank.bay.tracePointsAttente")
          : count === 1
            ? t("bank.bay.tracePointsUn")
            : fill("bank.bay.tracePoints", { n: String(count) }),
      at: endOf("points"),
    },
    {
      key: "note",
      on: endOf("grade") !== null,
      words: t(incomplete ? "bank.bay.traceNoteIncomplete" : "bank.bay.traceNote"),
      at: endOf("grade"),
    },
    { key: "avis", on: endOf("avis") !== null, words: t("bank.bay.traceAvis"), at: endOf("avis") },
  ];
  return (
    <ol data-bay="trace" data-testid="bank-hakili-trail">
      {lines.map((line) => (
        <li key={line.key} data-state={line.on ? "on" : "off"} data-testid={`bank-hakili-etape-${line.key}`}>
          <span>{line.words}</span>
          {line.on ? clock(line.at) : null}
        </li>
      ))}
    </ol>
  );
}

/** The proposal block (composer): what the server proposed, in the server's figures; or why it proposes nothing, in its words. */
function Proposal(props: { readonly analysis: AnalysisView; readonly onUse?: () => void }): ReactElement {
  const { offer, offerReason } = props.analysis;
  const cause = incompleteCauseOf(props.analysis.grade?.incomplete ?? null);
  // `#778` (D2 `bay.js:78`, `composerProposal`, `g.noRate`): the grid has no row for the file's letter. ONE boxed note and no sub-heading: the label, the served reason (the first
  // sentence) and D2's two fixed sentences, which the door does not serve (S5, from the catalogue).
  const letter = props.analysis.grade?.grade ?? null;
  const noGridRow =
    offer === null &&
    cause === null &&
    props.analysis.grade?.provisional !== true &&
    letter !== null &&
    props.analysis.gridMissingGrades.includes(letter);
  if (noGridRow) {
    return (
      <section data-bay="proposition" data-testid="bank-hakili-proposition">
        <p data-bay="note" data-tone="warn">
          <LabelledSentence
            stacked
            label={t("bank.bay.pasDeProposition")}
            words={`${asSentence(offerReason ?? "")} ${t("bank.bay.propositionSansTaux")}`}
          />
        </p>
      </section>
    );
  }
  // `#778` (D-1026-1): D2's incomplete note (`bay.js:77`) and provisional note (`bay.js:79`) are one boxed note with no heading either; « Proposition de Hakili » belongs to the proposal itself.
  const boxedNote = offer === null && (cause !== null || props.analysis.grade?.provisional === true);
  return (
    <section data-bay="proposition" data-testid="bank-hakili-proposition">
      {boxedNote ? null : <h3 data-bay="h3">{t("bank.hakili.proposition")}</h3>}
      {offer === null ? (
        cause === null ? (
          <p data-bay="note" data-tone="warn">
            <LabelledSentence
              stacked={boxedNote}
              label={t("bank.bay.pasDeProposition")}
              // `BAY-PROVISIONAL-WORDS` (S5, D2 `bay.js:79`, frame 33): D2 draws its own fixed sentence for the provisional note, keyed by the typed flag; the server's words stand only where D2 draws none.
              words={
                props.analysis.grade?.provisional === true
                  ? t("bank.bay.propositionProvisoire")
                  : (offerReason ?? "")
              }
            />
          </p>
        ) : (
          // D2 `bay.js:77` (`composerProposal`, frame 34): « Pas de proposition. » on its own line, then the incomplete note's short cause below it; the grid's adds where to enter it.
          <p data-bay="note" data-tone="warn">
            <LabelledSentence
              stacked
              label={t("bank.bay.pasDeProposition")}
              words={`${fill("bank.bay.propositionIncomplete", { court: cause.court })}${
                props.analysis.grade?.incomplete?.cause === "grille"
                  ? ` ${t("bank.bay.propositionChef")}`
                  : ""
              }`}
            />
          </p>
        )
      ) : offer.termBasis === null || offer.termBasisWords === null || offer.termBasisWords === "" ? (
        // A door that sends no basis: the figures alone, and no « propose » line , never a constant.
        <p data-bay="prop-line">
          {fill("bank.hakili.offreProposee", {
            duree: String(offer.durationMonths),
            taux: offer.gridRatePct.replace(".", ","),
            mensualite: formatMoney(offer.instalmentAmount),
            taeg: offer.taegPct.replace(".", ","),
            note: offer.gradeProposed ?? "",
          })}
        </p>
      ) : (
        // `TERM-FIT`, the designer's three lines , every figure as the offer sent it, nothing computed here: the term and its
        // rate, then why (the server's own words, set as a sentence), then the instalment and the TAEG. `termNote` is not shown.
        <div data-bay="prop">
          <p data-bay="prop-head" data-testid="bank-hakili-propose">
            {fill("bank.bay.proposeDuree", {
              n: String(offer.durationMonths),
              taux: offer.gridRatePct.replace(".", ","),
            })}
          </p>
          <p data-bay="prop-base" data-testid="bank-hakili-propose-base">
            {asSentence(offer.termBasisWords)}
          </p>
          <p data-bay="prop-line" data-testid="bank-hakili-propose-chiffres">
            {fill("bank.bay.proposeChiffres", {
              mensualite: formatMoney(offer.instalmentAmount),
              taeg: offer.taegPct.replace(".", ","),
            })}
          </p>
        </div>
      )}
      {offer === null || props.onUse === undefined ? null : (
        <Button size="sm" variant="secondary" onClick={props.onUse} data-testid="bank-hakili-reprendre">
          {t("bank.bay.reprendre")}
        </Button>
      )}
    </section>
  );
}

/** The grid row the Capacité was computed at, from the quotité point's operands: the highest row present , or `null`. */
export function capacityRateOf(analysis: AnalysisView): { grade: string; ratePct: string } | null {
  const evidence = analysis.points.find((one) => one.code === "quotiteCessible")?.evidence;
  if (evidence?.kind !== "figures" || evidence.operands === null) return null;
  const { rateSource, ratePct } = evidence.operands;
  return rateSource?.kind === "highestRow" && ratePct !== null ? { grade: rateSource.grade, ratePct } : null;
}

export function UnderwriterBay(props: UnderwriterBayProps): ReactElement {
  const { context, analysis } = props;
  const standing = runStanding(analysis, props.loaded, props.unavailable === true);
  const state = mascotStateFor(context, standing, analysis, {
    ...(props.flaggedPoints === undefined ? {} : { flaggedPoints: props.flaggedPoints }),
    ...(props.derogation === undefined ? {} : { derogation: props.derogation }),
    ...(props.composerFlagging === undefined ? {} : { composerFlagging: props.composerFlagging }),
  });
  const quiet = context === "conditions" || context === "quiet";
  const withAnalysis = analysis !== null && standing === "read" && !quiet;
  return (
    <div data-bay="root" data-ctx={context} data-state={state} data-testid="bank-bay">
      {/* The scene and the body are ONE scroll part; the question box is a footer outside it (designer's ruling (a): docked, always in view). */}
      <div data-bay="scroll">
        <Scene state={state} />
        <div data-bay="body">
          <div data-bay="who">
            <div>
              <p data-bay="eyebrow">{t("bank.bay.role")}</p>
              <h2 data-bay="h2">{t("bank.bay.nom")}</h2>
            </div>
            <StateBadge state={state} label={t(STATE_WORDS.get(state) ?? "bank.bay.etatRepos")} />
          </div>
          {standing === "reading" ? (
            <>
              <p data-bay="status">{props.loaded ? t("bank.hakili.enCours") : t("bank.bay.charge")}</p>
              {analysis === null ? null : <Trace analysis={analysis} reading />}
              <SkeletonRegion label={t("bank.session.chargement")}>
                <Skeleton />
                <Skeleton className="max-w-[80%]" />
                <Skeleton shape="block" />
                <Skeleton className="max-w-[60%]" />
              </SkeletonRegion>
            </>
          ) : null}
          {standing === "failed" ? (
            <>
              <div data-bay="fail" data-testid="bank-bay-echec">
                <b>{t("bank.bay.echecTitre")}</b>
                <p>{t("bank.bay.echecCorps")}</p>
              </div>
              <p data-bay="meta">{t("bank.bay.echecImmobile")}</p>
            </>
          ) : null}
          {standing === "unavailable" ? (
            <div data-bay="fail" data-testid="bank-bay-indisponible">
              <b>{t("bank.bay.erreurTitre")}</b>
              <p>{t("bank.bay.erreurCorps")}</p>
              {props.onRetry === undefined ? null : (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={props.onRetry}
                  data-testid="bank-bay-reessayer"
                >
                  {t("common.action.reessayer")}
                </Button>
              )}
            </div>
          ) : null}
          {standing === "none" ? (
            <p data-bay="status" data-testid="bank-bay-sans-analyse">
              {props.sentence ?? t("bank.hakili.aucuneAnalyse")}
            </p>
          ) : null}
          {context === "conditions" && standing !== "reading" ? (
            // D2 frame 46 (`quietBay`): the dates the platform holds, in place of the calm sentence.
            <section data-testid="bank-bay-dates">
              <h3 data-bay="h3">{t("bank.bay.datesTitre")}</h3>
              <ul data-bay="watch">
                <li>
                  <b>{t("bank.bay.dateRapport")}</b>
                  <span>
                    {props.watch?.reportValidUntil === null || props.watch === undefined
                      ? t("bank.bay.dateAbsente")
                      : fill("bank.bay.valableJusquau", {
                          date: formatDate(new Date(`${props.watch.reportValidUntil}T00:00:00Z`)).replace(
                            / \d{4}$/u,
                            "",
                          ),
                        })}
                  </span>
                </li>
                <li>
                  <b>{t("bank.bay.dateProforma")}</b>
                  <span>{t("bank.bay.dateAbsente")}</span>
                </li>
              </ul>
              <p data-bay="meta">{t("bank.bay.datesNote")}</p>
            </section>
          ) : quiet && standing !== "reading" ? (
            <p data-bay="status" data-quiet="">
              {t("bank.bay.calme")}
            </p>
          ) : null}
          {withAnalysis ? (
            <>
              {context === "nouveau" ? <Trace analysis={analysis} reading={false} /> : null}
              <GradeCard
                grade={analysis.grade}
                missingGrades={analysis.gridMissingGrades}
                capacityRate={capacityRateOf(analysis)}
                sentence={props.sentence}
                {...(props.onPolitique === undefined ? {} : { onPolitique: props.onPolitique })}
              />
              {context === "composer" ? (
                <Proposal
                  analysis={analysis}
                  {...(props.onUseProposal === undefined ? {} : { onUse: props.onUseProposal })}
                />
              ) : null}
              <OpinionPanel
                {...(context === "decision" ? { readOnly: true } : {})}
                opinion={analysis.opinion}
                sentence={props.sentence}
                busy={props.busy}
              />
              {/* D2's order and D2's contexts: the register and the second look close the review's and the decision's bay; the composer has neither. */}
              {context === "composer" ? null : <Register analysis={analysis} />}
              {context === "composer" || analysis.grade === null ? null : (
                <SecondLook
                  components={analysis.grade.components}
                  look={analysis.grade.secondLook}
                  earlier={analysis.grade.earlierSecondLook ?? null}
                  readOnly={context === "decision"}
                  {...(props.onSecondLook === undefined ? {} : { onSecondLook: props.onSecondLook })}
                />
              )}
            </>
          ) : null}
        </div>
      </div>
      {props.question === undefined || standing === "failed" ? null : (
        <div data-bay="ask-dock">{props.question}</div>
      )}
    </div>
  );
}
